(() => {
  if (customElements.get('cappafe-category-menu')) return;
  class CategoryDisclosure extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.querySelector('details');
      this.trigger = this.root.querySelector('summary');
      const panel = this.trigger.nextElementSibling;
      panel.id = this.root.id + '-content';
      this.trigger.setAttribute('role', 'button');
      this.trigger.setAttribute('aria-controls', panel.id);
      this.trigger.setAttribute('aria-expanded', String(this.root.open));
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' || !matchMedia('(hover: hover)').matches) return;
        clearTimeout(this.hoverClose);
        this.hoverOpen = setTimeout(() => { this.root.open = true; }, 120);
      });
      this.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.hoverOpen);
        this.hoverClose = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.root.open = false;
        }, 220);
      });
      this.root.addEventListener('toggle', event => {
        if (event.target !== this.root) return;
        this.trigger.setAttribute('aria-expanded', String(this.root.open));
        if (this.root.open) this.closeOthers();
        const header = this.closest('.header-wrapper');
        const anyOpen = !!header.querySelector('.cf-root[open]');
        header.classList.toggle('cf-category-open', anyOpen);
        header.preventHide = anyOpen;
        header.querySelector('.drawer-menu--on-scroll')?.classList.toggle('drawer-menu--on-scroll--paused', anyOpen);
        if (anyOpen) document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.round(header.getBoundingClientRect().bottom)}px`);
      });
      this.addEventListener('keydown', event => {
        if (event.key !== 'Tab' || !this.root.open) return;
        const focusable = [...this.querySelectorAll('summary,button,a[href]')].filter(element => element.tabIndex >= 0 && element.getClientRects().length);
        if ((!event.shiftKey && event.target === focusable.at(-1)) || (event.shiftKey && event.target === this.trigger)) this.root.open = false;
      });
      this.outsideClick = event => { if (!this.contains(event.target)) this.root.open = false; };
      document.addEventListener('click', this.outsideClick);
    }
    closeOthers() {
      document.querySelectorAll('.header__inline-menu .mega-menu[open]').forEach(details => {
        if (details === this.root) return;
        details.open = false;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        const oldMenu = details.closest('header-menu');
        if (oldMenu) { clearTimeout(oldMenu.hoverIntentTimer); oldMenu.classList.remove('open'); }
      });
    }
    disconnectedCallback() {
      clearTimeout(this.hoverOpen);
      clearTimeout(this.hoverClose);
      document.removeEventListener('click', this.outsideClick);
    }
  }
  customElements.define('cappafe-category-disclosure', CategoryDisclosure);
  class CategoryMenu extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('.cf-root');
      this.querySelector('.cf-close').addEventListener('click', () => this.close());
      this.groups = [...this.querySelectorAll('[data-group]')];
      this.setupTabs(this.querySelector('.cf-group-tabs'), this.groups, button => this.selectGroup(button));
      this.querySelectorAll('.cf-group-panel').forEach(panel => {
        const buttons = [...panel.querySelectorAll('[data-sub]')];
        this.setupTabs(panel.querySelector('.cf-sub-tabs'), buttons, button => this.selectSub(panel, button));
        if (buttons.length) this.selectSub(panel, buttons[0]);
      });
      if (this.groups.length) this.selectGroup(this.groups[0]);
      this.addEventListener('keyup', event => {
        if (event.key === 'Escape') { event.stopPropagation(); this.close(); }
      });
      this.root.addEventListener('keyup', event => {
        if (event.key === 'Escape') { event.stopPropagation(); this.close(); }
      });
      this.classList.add('is-enhanced');
    }
    setupTabs(list, buttons, select) {
      if (!list) return;
      list.setAttribute('role', 'tablist');
      list.setAttribute('aria-orientation', 'vertical');
      buttons.forEach(button => {
        button.setAttribute('role', 'tab');
        button.addEventListener('click', () => select(button));
        // Desktop: open a group/sub-category on hover (short intent delay so diagonal mouse moves don't flicker).
        button.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.hoverTab);
          this.hoverTab = setTimeout(() => select(button), 90);
        });
        button.addEventListener('pointerleave', () => clearTimeout(this.hoverTab));
        button.addEventListener('keydown', event => {
          const index = buttons.indexOf(button);
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % buttons.length;
          if (event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          if (next !== undefined) { event.preventDefault(); select(buttons[next]); buttons[next].focus(); }
          if (event.key === 'ArrowRight') {
            const panel = this.querySelector('#' + button.getAttribute('aria-controls'));
            const target = panel?.querySelector('[role="tab"][aria-selected="true"], a');
            if (target) { event.preventDefault(); target.focus(); }
          }
        });
      });
    }
    selectGroup(button) {
      this.groups.forEach(item => {
        const active = item === button;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-group-panel]').forEach(panel => {
        panel.hidden = panel.dataset.groupPanel !== button.dataset.group;
        panel.setAttribute('role', 'tabpanel');
      });
    }
    selectSub(group, button) {
      group.querySelectorAll('[data-sub]').forEach(item => {
        const active = item === button;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      group.querySelectorAll('[data-sub-panel]').forEach(panel => {
        panel.hidden = panel.dataset.subPanel !== button.dataset.sub;
        panel.setAttribute('role', 'tabpanel');
      });
    }
    close() {
      this.root.open = false;
      const trigger = this.root.querySelector('summary');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
  }
  customElements.define('cappafe-category-menu', CategoryMenu);
  class CategoryMobile extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener('keyup', event => {
        if (event.key !== 'Escape') return;
        const open = event.target.closest('details[open]');
        if (open && this.contains(open)) {
          event.stopPropagation(); open.open = false; open.querySelector('summary').focus();
        }
      });
      this.querySelectorAll('details').forEach(detail => {
        detail.addEventListener('toggle', () => {
          const summary = detail.querySelector('summary');
          summary.setAttribute('aria-expanded', String(detail.open));
          summary.querySelector('span').textContent = detail.open ? '−' : '+';
        });
      });
    }
  }
  customElements.define('cappafe-category-mobile', CategoryMobile);
})();
